import { useEffect, useRef } from 'react';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { initCloudflareAnalytics, removeCloudflareAnalytics } from '@/lib/cloudflareAnalytics';

/**
 * Manages Cloudflare Web Analytics script injection based on cookie consent.
 * The script is only loaded when the user has granted analytics consent.
 * If consent is revoked, the script is removed from the DOM.
 */
export default function ConsentAwareCloudflareAnalytics() {
  const { consent, hasConsented } = useCookieConsent();
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    // Check if consent has been determined AND analytics is explicitly allowed
    // Only load script if BOTH conditions are true:
    // 1. hasConsented === true (consent has been determined)
    // 2. consent.analytics === true (user explicitly granted analytics consent)
    const shouldLoadScript = hasConsented && consent.analytics === true;

    if (shouldLoadScript && !scriptLoadedRef.current) {
      // User has granted analytics consent - inject the script
      initCloudflareAnalytics();
      scriptLoadedRef.current = true;
    } else if (!shouldLoadScript) {
      // User has not granted analytics consent or consent not yet determined - remove the script
      removeCloudflareAnalytics();
      scriptLoadedRef.current = false;
    }
  }, [consent.analytics, hasConsented]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      removeCloudflareAnalytics();
      scriptLoadedRef.current = false;
    };
  }, []);

  // This component doesn't render anything, it only manages the script
  return null;
}
